import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { useDndMonitor, useDroppable, DragOverEvent } from '@dnd-kit/core';
import { IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconType from '../../InputComponents/IconType';
import { ECategoryType, EDropCategory } from '../../../data/Constants';
import { getIconType, isValidItemDrop } from '../../../services/CategoryHelper';

import type { ISection, SortableCategoryProps } from '../../../../types';

/**
 * Determines the CSS class to apply based on drag state
 * @param isDragOver - Whether an element is being dragged over this component
 * @param isValidDragOver - Whether the drag operation is valid for this component
 * @param isOver - Whether the mouse is hovering over this component
 * @returns The appropriate CSS class name based on the drag state
 */
const getDragStateClass = (
  isDragOver: boolean,
  isValidDragOver: boolean,
  isOver: boolean
): string => {
  if (isDragOver) {
    return isValidDragOver ? 'over' : 'invalid';
  }
  if (isOver) {
    return 'hover';
  }
  return '';
};

/**
 * Helper to render category actions
 */
const renderCategoryActions = (
  section: ISection,
  editView: boolean,
  handleAddClick: any,
  handleEditClick: any,
  handleDeleteClick: any
) => {
  if (!editView) return null;
  return (
    <div className="category-actions">
      {section?.id && !section?.hideAddIcon && (
        <IconButton size="small" onClick={handleAddClick} className="action-button">
          <AddIcon fontSize="small" />
        </IconButton>
      )}
      {section?.type === ECategoryType.MultiNumeric && (
        <IconButton
          size="small"
          onClick={handleEditClick}
          className="action-button"
          data-testid="editSection"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      {(section?.id || section.type) && (
        <IconButton
          size="small"
          onClick={handleDeleteClick}
          className="action-button"
          data-testid="deleteContentOrSection"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
};

const getContentStyleClasses = (
  section: ISection,
  selectedSectionId: string | undefined,
  isParentSelected: boolean
) =>
  [
    'category-content',
    section.id === selectedSectionId ? 'selected' : '',
    isParentSelected ? 'parent-selected' : '',
  ]
    .filter(Boolean)
    .join(' ');

/**
 * A draggable and sortable category component that supports nested subcategories
 * Uses dnd-kit for drag and drop functionality
 * @param props - Component properties
 * @param props.section - The section data to display
 * @param props.selectedSectionId - The currently selected section ID
 * @param props.onSelect - Callback function when a category is selected
 * @param props.onAddSection - Optional callback function to add a new section
 * @param props.onDeleteSection - Optional callback function to delete a section
 * @param props.depth - The nesting depth level, defaults to 0 for top level
 * @returns A sortable category component with drag-drop and collapse functionality
 */
export const SortableCategory: React.FC<SortableCategoryProps> = ({
  section,
  selectedSectionId,
  onAddSection,
  onDeleteSection,
  depth = 0,
  isParentSelected = false,
  editView = false,
}) => {
  const [isValidDragOver, setIsValidDragOver] = useState<boolean>(true);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);

  // Droppable hook for receiving drops
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `${EDropCategory.Category}-${section.id}`,
    data: {
      type: `${EDropCategory.Category}`,
      container: `${EDropCategory.Category}-${section.id}`,
      sectionId: section.id,
      itemType: section.type,
      itemName: section.name,
    },
  });

  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
  } = useSortable({
    id: `${EDropCategory.Category}-${section.id}`,
    data: {
      type: `${EDropCategory.Category}`,
      container: `${EDropCategory.Category}-${section.id}`,
      sectionId: section.id,
      itemType: section.type,
      itemName: section.name,
    },
  }); /**
   * Sets both sortable and droppable refs to the same DOM element
   * This combines drag-and-drop functionality with sortable behavior
   * @param element - The DOM element to attach both refs to
   */
  const setCombinedNodeRef = (element: HTMLElement | null) => {
    setSortableRef(element);
    setDroppableRef(element);
  };

  // Get appropriate drag state class
  const dragStateClass = getDragStateClass(isDragOver, isValidDragOver, isOver);
  const categoryStyleClasses = ['category-item', dragStateClass].filter(Boolean).join(' ');

  /**
   * Monitor for drag and drop events
   * Updates component state based on drag operations
   */
  useDndMonitor(
    editView
      ? {
          /**
           * Handles drag over events
           * Determines if the current drag operation is valid for this category
           * @param event - The drag over event from dnd-kit
           */
          onDragOver(event: DragOverEvent) {
            const { active, over } = event;
            if (!over) {
              setIsDragOver(false);
              return;
            }

            // Check if we're directly over this specific category
            const isOverThisCategory = over.id === `${EDropCategory.Category}-${section.id}`;
            const draggedType = active?.data?.current?.container;
            const isCategoryDraggedType = active?.data?.current?.type;
            if (isOverThisCategory) {
              setIsDragOver(true);
              // Only validate for assigned/unassigned items, not for category drag
              if (
                draggedType === EDropCategory.Assigned ||
                draggedType === EDropCategory.Unassigned
              ) {
                setIsValidDragOver(isValidItemDrop(section.type, active.data?.current?.itemType));
              } else if (isCategoryDraggedType === EDropCategory.Category) {
                // If dragging a category over other category, always valid (no red border)
                setIsValidDragOver(true);
              }
            } else if (isDragOver) {
              // Only reset if we were previously over this category
              setIsDragOver(false);
            }
          },
          /**
           * Resets component state when drag operation ends
           */
          onDragEnd() {
            setIsDragOver(false);
            setIsValidDragOver(true);
          },
          /**
           * Resets component state when drag operation is cancelled
           */
          onDragCancel() {
            setIsDragOver(false);
            setIsValidDragOver(true);
          },
        }
      : {} // If not edit mode, don't attach drag handlers
  );

  /**
   * Handles click on the add button
   * Prevents event propagation and calls the onAddSection callback with the current section
   * @param e - The mouse event that triggered the action
   */
  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddSection?.(section);
  };

  /**
   * Handles click on the delete button
   * Prevents event propagation and calls the onDeleteSection callback with the current section
   * @param e - The mouse event that triggered the action
   */
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDeleteSection?.(section);
  };

  /**
   * Handles click on the edit button
   * Prevents event propagation and calls the onAddSection callback with the current section
   * modified with Edit action and MultiNumeric type
   * @param e - The mouse event that triggered the action
   */
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddSection?.({
      ...section,
      action: 'Edit',
      type: ECategoryType.MultiNumeric,
    });
  };

  const contentStyleClasses = getContentStyleClasses(section, selectedSectionId, isParentSelected);

  /**
   * Renders an appropriate icon for the feature with a tooltip
   * @param item - The item (section or feature) to render an icon for
   * @returns A React component with an icon and tooltip
   */
  const featureIcon = (item: any) => {
    return (
      <Tooltip title={item?.familyType || item?.type || item?.name || ''}>
        <span className="feature-icon">
          <IconType type={getIconType(item)} />
        </span>
      </Tooltip>
    );
  };

  return (
    <div
      className="category-wrapper"
      style={{ paddingLeft: `${depth * 20}px` }}
      data-section-depth={depth}
    >
      <div
        ref={setCombinedNodeRef}
        className={categoryStyleClasses}
        data-type={EDropCategory.Category}
        data-section-id={section.id}
        data-section-name={section.name}
      >
        <div className={contentStyleClasses}>
          <div className="category-title">
            <div
              {...(editView ? attributes : {})}
              {...(editView ? listeners : {})}
              className={`drag-handle${editView ? '' : ' drag-disabled'}`}
              style={{ cursor: editView ? 'grab' : 'not-allowed' }}
            >
              {featureIcon(section)}
              <span className="feature-name">{section.name}</span>
            </div>
          </div>
          {/* Always show action buttons for accessibility */}
          <div className="category-actions-wrapper">
            {renderCategoryActions(
              section,
              editView,
              handleAddClick,
              handleEditClick,
              handleDeleteClick
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
